/// <amd-module name="Test/FailedTestsMetricView/Services/FailedTestsSourceService" />
import * as PipelineEntities from "Analytics/Shared/Common/PipelineEntities";
import { IAnalyticsExtensionStateService } from "Analytics/Shared/ExtensionService/AnalyticsExtensionService";
import { axServiceInstanceId, emsServiceInstanceId } from "Test/Analytics/Common/Constants";
import { WorkflowConfigurationProps } from "Test/Analytics/Common/Definitions";
import * as CommonTypes from "Test/Analytics/Common/Types";
import { getTestAnalyticsPageUrl } from "Test/Analytics/Common/Utils/TestAnalyticsURLUtils";
import { FilterState } from "Test/common-util/Contracts/Filters";
import * as TCMContracts from "Test/common-util/Contracts/Test";
import { FilterValueItem } from "Test/common-util/TestResults.Filtering.Common";
import * as Resources from "Test/FailedTestsMetricView/Resources";
import { FailedTestsMetricSource } from "Test/FailedTestsMetricView/Sources/FailedTestsMetricSource";
import * as Utils_String from "VSS/Core/Util/String";
import { IVssPageContext, Services, TeamFoundationHostType, VssService } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IVssLocationService } from "VSS/Platform/Location";

export class FailedTestsSourceService extends VssService implements CommonTypes.IFailedTestsSource {
    private _failedTestsSource: FailedTestsMetricSource;
    private _pageContext: IVssPageContext;

    public initialize(command: string, pageContext: IVssPageContext) {
        this._pageContext = pageContext;

        if (isFeatureFlagEnabled(pageContext, PipelineEntities.RenameBuildEntitiesFeatureFlag)) {
            PipelineEntities.switchEntityAndAttributeNames();
        }

        this._failedTestsSource = new FailedTestsMetricSource(pageContext);
    }

    public async getTestAnalyticsLinkData(testReportContext: CommonTypes.ITestReportContext): Promise<CommonTypes.ITestAnalyticsLinkData> {
        const testAnalyticsLinkData = {} as CommonTypes.ITestAnalyticsLinkData;

        const vssLocationService: IVssLocationService = this._pageContext.getService<IVssLocationService>("IVssLocationService");

        const emsServicePromise: Promise<string> = vssLocationService.getServiceLocationAsync(
            emsServiceInstanceId,
            TeamFoundationHostType.ProjectCollection
        );
        const axServicePromise: Promise<string> = vssLocationService.getServiceLocationAsync(
            axServiceInstanceId,
            TeamFoundationHostType.ProjectCollection
        );

        return Promise.all([emsServicePromise, axServicePromise]).then(async result => {
            try {
                const contributionService: IVssContributionService = this._pageContext.getService<IVssContributionService>("IVssContributionService");
                await contributionService.getContributionAsync("ms.vss-analyticsshared-web.analytics-extension-service");

                const analyticsExtensionStateService = this._pageContext.getService<IAnalyticsExtensionStateService>(
                    "IAnalyticsExtensionStateService"
                );
                const isAnalyticsEnabled = await analyticsExtensionStateService.isAnalyticsEnabled();
                if (isAnalyticsEnabled) {
                    const confValues: CommonTypes.IReportConfiguration = {
                        groupBy: CommonTypes.GroupBy.None,
                        trendBy: CommonTypes.TrendBy.Days,
                        period: CommonTypes.Period.Days_14,
                        outcomes: [],
                        configuredFilters: this.getConfiguredFilters(testReportContext)
                    };
                    const failedTestsData = await this.queryTotalFailingTestsMetricData(testReportContext, confValues);
                    if (failedTestsData.totalFailingTestsCount > 0) {
                        testAnalyticsLinkData.testAnalyticsLinkMessage = Utils_String.localeFormat(
                            Resources.TestAnalyticsLinkMessage,
                            failedTestsData.totalFailingTestsCount,
                            failedTestsData.totalFailingTestsCount === 1 ? Resources.TestSingularText : Resources.TestPluralText
                        );
                    }
                }
            } catch (error) {
                if (error.message) {
                    console.error(JSON.stringify(error.message));
                } else {
                    console.error(JSON.stringify(error));
                }
            }

            testAnalyticsLinkData.testAnalyticsLink = getTestAnalyticsPageUrl(this._pageContext, testReportContext);
            return testAnalyticsLinkData;
        });
    }

    private getConfiguredFilters(testReportContext: CommonTypes.ITestReportContext): FilterState {
        return testReportContext.contextType === TCMContracts.TestResultsContextType.Build
            ? {
                  [CommonTypes.Filter.Workflow]: {
                      values: [
                          new FilterValueItem(
                              CommonTypes.Workflow.Build.toString(),
                              new WorkflowConfigurationProps().options[CommonTypes.Workflow.Build]
                          )
                      ]
                  }
              }
            : {
                  [CommonTypes.Filter.Workflow]: {
                      values: [
                          new FilterValueItem(
                              CommonTypes.Workflow.Release.toString(),
                              new WorkflowConfigurationProps().options[CommonTypes.Workflow.Release]
                          )
                      ]
                  }
              };
    }

    private queryTotalFailingTestsMetricData(
        testReportContext: CommonTypes.ITestReportContext,
        confValues: CommonTypes.IReportConfiguration
    ): Promise<CommonTypes.IFailedCardMetricData> {
        return this._failedTestsSource.queryTotalFailingTestsMetricData(testReportContext, confValues);
    }
}

Services.add("FailedTestsSourceService", { serviceFactory: FailedTestsSourceService });
